import { Component, Input, OnChanges, SimpleChanges, signal, ChangeDetectionStrategy } from '@angular/core';
import { NgStyle } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { CanalCreatedData } from './screens.types';

type MsgType = 'system' | 'sent' | 'received';
type ThreadStatus = 'critique' | 'surveillance' | 'info' | 'general';

interface Thread {
  id: string;
  title: string;
  subtitle: string;
  memberCount: number;
  status: ThreadStatus;
  lastMessage: string;
  lastTime: string;
  unread: number;
  presenceEstimee?: number;
  tensionMinutes?: number | null;
}

interface Message {
  id: number;
  type: MsgType;
  sender?: string;
  site?: string;
  text: string;
  time: string;
  isAlert?: boolean;
}

@Component({
  selector: 'app-message-screen',
  standalone: true,
  imports: [NgStyle, TranslatePipe],
  templateUrl: './message-screen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./message-screen.component.scss'],
})
export class MessageScreenComponent implements OnChanges {
  @Input() openCanal: CanalCreatedData | null = null;
  @Input() openDirectThread: string | null = null;

  readonly selectedThread = signal<Thread | null>(null);

  threads: Thread[] = [
    {
      id: 'nantes',
      title: 'Nantes Centre',
      subtitle: 'Tension critique · 8 membres',
      memberCount: 8,
      status: 'critique',
      lastMessage: 'Dispo pour soutien téléphonique si besoin.',
      lastTime: '15:13',
      unread: 0,
    },
    {
      id: 'carquefou',
      title: 'Carquefou',
      subtitle: 'Surveillance · 5 membres',
      memberCount: 5,
      status: 'surveillance',
      lastMessage: 'On a 16 patients en attente, ça monte.',
      lastTime: '15:28',
      unread: 2,
    },
    {
      id: 'general',
      title: 'Réseau général',
      subtitle: 'Tous les sites · 12 membres',
      memberCount: 12,
      status: 'general',
      lastMessage: 'Réunion de coordination à 16h pour le bilan.',
      lastTime: '14:45',
      unread: 1,
    },
  ];

  messagesByThread: Record<string, Message[]> = {
    nantes: [
      { id: 1, type: 'system',   text: 'Nantes Centre passe en tension critique',             time: '15:04', isAlert: true  },
      { id: 2, type: 'received', sender: 'Paul R.',   site: 'Nantes Centre', text: 'On a 38 patients en salle, ça déborde. Besoin de renfort !',     time: '15:06' },
      { id: 3, type: 'received', sender: 'Marie L.',  site: 'Rezé',          text: 'Je suis dispo, je peux être là d\'ici 20 min.',                   time: '15:08' },
      { id: 4, type: 'received', sender: 'Thomas M.', site: 'Orvault',       text: 'Je prends vos résultats depuis ici, envoyez-moi les dossiers.',   time: '15:09' },
      { id: 5, type: 'sent',     text: 'Je transfère les appels vers Orvault. Thomas, tu peux absorber ?',                                            time: '15:10' },
      { id: 6, type: 'received', sender: 'Thomas M.', site: 'Orvault',       text: 'Oui, pas de souci.',                                              time: '15:11' },
      { id: 7, type: 'system',   text: '✓ Marie L. confirmée en route → Nantes Centre',        time: '15:12', isAlert: false },
      { id: 8, type: 'received', sender: 'Sophie D.', site: 'Vertou',        text: 'Dispo pour soutien téléphonique si besoin.',                      time: '15:13' },
    ],
    carquefou: [
      { id: 1, type: 'system',   text: 'Carquefou passe en surveillance',                      time: '15:20', isAlert: true  },
      { id: 2, type: 'received', sender: 'Claire B.', site: 'Saint-Herblain', text: 'Je surveille aussi, on a pareil ici depuis 30 min.',             time: '15:24' },
      { id: 3, type: 'received', sender: 'Paul R.',   site: 'Carquefou',      text: 'On a 16 patients en attente, ça monte.',                         time: '15:28' },
    ],
    general: [
      { id: 1, type: 'received', sender: 'Thomas M.', site: 'Orvault',       text: 'Réunion de coordination à 16h pour le bilan.',                    time: '14:45' },
      { id: 2, type: 'sent',     text: 'Je confirme ma présence.',                                                                                     time: '14:47' },
    ],
  };

  private readonly statutToThreadStatus: Record<string, ThreadStatus> = {
    critique: 'critique',
    moderee: 'surveillance',
    fluide: 'general',
    ferme: 'general',
  };

  private readonly statutToLabel: Record<string, string> = {
    critique: 'Tension critique',
    moderee: 'En surveillance',
    fluide: 'Fluide',
    ferme: 'Fermé',
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['openDirectThread'] && this.openDirectThread) {
      const nom = this.openDirectThread;
      const threadId = `dm-${nom.toLowerCase().replace(/\s+/g, '-')}`;
      const existing = this.threads.find(t => t.id === threadId);
      if (existing) {
        this.selectedThread.set(existing);
      } else {
        const now = new Date();
        const time = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        const newThread: Thread = {
          id: threadId,
          title: nom,
          subtitle: 'Conversation directe',
          memberCount: 1,
          status: 'general',
          lastMessage: 'Démarrez la conversation…',
          lastTime: time,
          unread: 0,
        };
        this.messagesByThread[newThread.id] = [];
        this.threads = [newThread, ...this.threads];
        this.selectedThread.set(newThread);
      }
    }
    if (changes['openCanal'] && this.openCanal) {
      const d = this.openCanal;
      const now = new Date();
      const time = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      const membersLabel = d.memberCount > 0
        ? `${d.memberCount} membre${d.memberCount > 1 ? 's' : ''}`
        : 'Aucun membre';
      const isCritique = d.siteStatut === 'critique';
      const newThread: Thread = {
        id: `canal-${now.getTime()}`,
        title: d.name,
        subtitle: `${this.statutToLabel[d.siteStatut]} · ${membersLabel}`,
        memberCount: d.memberCount,
        status: this.statutToThreadStatus[d.siteStatut] ?? 'general',
        lastMessage: d.memberCount > 0 ? `Membres : ${d.memberNames.join(', ')}` : 'Canal ouvert',
        lastTime: time,
        unread: 0,
        ...(isCritique ? { presenceEstimee: d.presenceEstimee, tensionMinutes: d.tensionMinutes } : {}),
      };
      const systemMsg: Message = {
        id: 1,
        type: 'system',
        text: `Canal "${d.name}" créé`,
        time,
        isAlert: d.siteStatut === 'critique',
      };
      this.messagesByThread[newThread.id] = [systemMsg];
      this.threads = [newThread, ...this.threads];
      this.selectedThread.set(newThread);
    }
  }

  private readonly avatarPalette: Record<string, { bg: string; color: string }> = {
    P: { bg: '#FEE2E2', color: '#DC2626' },
    M: { bg: '#D1FAE5', color: '#059669' },
    T: { bg: '#DBEAFE', color: '#2563EB' },
    S: { bg: '#EDE9FE', color: '#7C3AED' },
    C: { bg: '#FEF3C7', color: '#D97706' },
  };

  avatarStyle(sender: string): { background: string; color: string } {
    const key = sender[0].toUpperCase();
    const p = this.avatarPalette[key] ?? { bg: '#F3F4F6', color: '#6B7280' };
    return { background: p.bg, color: p.color };
  }

  selectThread(thread: Thread) { this.selectedThread.set(thread); }
  back() { this.selectedThread.set(null); }

  currentMessages(): Message[] {
    const t = this.selectedThread();
    return t ? (this.messagesByThread[t.id] ?? []) : [];
  }
}